/* ==========================================================================
   TRE EMME — CX commerce: cart bridge, quick add
   Needs cx-core.js (window.CX) and Tre Emme's _cart.js (window.themeCart).
   ========================================================================== */
(() => {
  'use strict';

  const { $, $$, toast } = window.CX;

  /* Cart — Tre Emme's drawer (section-drawer + _cart.js) stays the source of truth:
     window.themeCart.add() posts to /cart/add.js, re-renders the drawer through the Section Rendering API
     and opens it. Here we only add the CX touches: count bump, scroll lock, focus return, error toasts. */
  const drawer = () => document.getElementById('drawer');
  let lastFocus = null;

  const ERRORS = {
    422: 'Quantità non disponibile per questa taglia.',
    500: 'Si è verificato un errore, riprova.',
    0: 'Connessione assente, riprova.',
  };

  // One entry point for every "add to cart" in the CX layer. Resolves to true when added, null otherwise.
  // A second identical add while the first is running (double tap on PDP, sticky bar, quick add) reuses it:
  // the drawer shows no quantities, so a doubled line would go unnoticed to checkout.
  const inflight = new Map();
  const cartAdd = (items) => {
    const key = JSON.stringify(Array.isArray(items) ? items : [items]);
    if (!inflight.has(key)) inflight.set(key, addItems(items).finally(() => inflight.delete(key)));
    return inflight.get(key);
  };
  const addItems = async (items) => {
    const list = Array.isArray(items) ? items : [items];
    try {
      if (window.themeCart && window.themeCart.add) {
        // _cart.js adds one variant per call; it re-renders and opens the drawer after each
        for (const item of list) {
          const res = await window.themeCart.add(item.id, item.quantity || 1);
          if (!res.ok) throw new Error(ERRORS[res.status] || ERRORS[500]);
        }
        return true;
      }
      const r = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: list }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.description || ERRORS[r.status] || ERRORS[500]);
      }
      window.location.href = '/cart';
      return true;
    } catch (err) {
      toast(err.message || ERRORS[500], { type: 'error' });
      return null;
    }
  };
  window.CX.cartAdd = cartAdd;

  // Count: _cart.js writes the number of lines into .header__cart-count; mirror it for the CX header
  const watchCount = (el) => {
    let prev = Number(el.textContent) || 0;
    new MutationObserver(() => {
      const count = Number(el.textContent) || 0;
      el.setAttribute('data-cx-count', count);
      el.classList.toggle('is-hidden', !count);
      el.classList.remove('hidden');
      if (count > prev) {
        el.classList.remove('is-bump');
        void el.offsetWidth;
        el.classList.add('is-bump');
      }
      prev = count;
    }).observe(el, { childList: true, characterData: true, subtree: true });
  };
  $$('.js-cart-count').forEach(watchCount);

  // Drawer open/close: lock the page and give focus back to the control that opened it
  document.addEventListener('click', (e) => { const c = e.target.closest('a, button'); if (c && !(drawer() && drawer().contains(c))) lastFocus = c; }, true);
  const d0 = drawer();
  if (d0) {
    new MutationObserver(() => {
      const open = d0.classList.contains('active');
      document.documentElement.classList.toggle('cx-lock', open);
      document.body.classList.toggle('is-drawer-open', open);
      if (!open && lastFocus && lastFocus.isConnected && d0.contains(document.activeElement)) lastFocus.focus({ preventScroll: true });
    }).observe(d0, { attributes: true, attributeFilter: ['class'] });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && d0.classList.contains('active')) d0.classList.remove('active');
    });
  }

  /* Quick add: a size picker built from the tapped card (bottom sheet on phones, centred popup on desktop) */
  const phone = window.matchMedia('(max-width: 767px)');
  const sheet = document.getElementById('cx-qsheet');
  const openSheet = (card) => {
    const source = card && $('.cx-card__quick:not(.cx-card__quick--single)', card);
    if (!sheet || !source || !window.CX.openOverlay) return false;
    const img = $('.cx-card__img:not(.cx-card__img--alt)', card);
    const thumb = $('[data-cx-qsheet-thumb]', sheet);
    thumb.innerHTML = img ? `<img src="${img.currentSrc || img.src}" alt="">` : '';
    $('[data-cx-qsheet-vendor]', sheet).textContent = ($('.cx-card__vendor', card) || {}).textContent || '';
    const link = $('.cx-card__titlelink', card);
    const title = $('[data-cx-qsheet-title]', sheet);
    title.textContent = link ? link.textContent.trim() : '';
    title.href = link ? link.href : '#';
    $('[data-cx-qsheet-price]', sheet).innerHTML = ($('.cx-card__price', card) || {}).innerHTML || '';
    const form = source.cloneNode(true);
    form.removeAttribute('id');
    form.classList.remove('is-done');
    const body = $('[data-cx-qsheet-body]', sheet);
    body.replaceChildren(form);
    window.CX.openOverlay(sheet, $('[data-cx-quick-toggle]', card));
    return true;
  };

  /* Quick add from the product card */
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-cx-quick]');
    if (!form) return;
    e.preventDefault();
    const btn = e.submitter && e.submitter.name === 'id' ? e.submitter : null;
    if (!btn || btn.disabled) return;
    // From the sheet: close it first, the cart drawer takes over
    if (sheet && sheet.contains(form) && window.CX.closeOverlay) window.CX.closeOverlay(false);
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');

    const items = [{ id: Number(btn.value), quantity: 1 }];
    const done = () => {
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-busy');
    };

    cartAdd(items).then((res) => {
      done();
      if (!res) return;
      const card = form.closest('[data-cx-card]');
      form.classList.add('is-done');
      card && card.classList.add('is-added');
      setTimeout(() => {
        form.classList.remove('is-done');
        if (!card) return;
        card.classList.remove('is-quick', 'is-added');
        const t = $('[data-cx-quick-toggle]', card);
        t && t.setAttribute('aria-expanded', 'false');
      }, 1600);
    }).catch(done);
  });

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-cx-quick]')) return;
    const toggle = e.target.closest('[data-cx-quick-toggle]');
    $$('.cx-card.is-quick').forEach((c) => {
      if (!toggle || !c.contains(toggle)) {
        c.classList.remove('is-quick');
        const t = $('[data-cx-quick-toggle]', c);
        t && t.setAttribute('aria-expanded', 'false');
      }
    });
    if (!toggle) return;
    const card = toggle.closest('[data-cx-card]');
    // Quick add opens the size popup everywhere: bottom sheet on phones, centred dialog from tablet up
    if (openSheet(card)) return;
    const on = card.classList.toggle('is-quick');
    toggle.setAttribute('aria-expanded', String(on));
    // Keyboard users land on the first size once the sheet is visible
    if (on && e.detail === 0) {
      const first = $('.cx-size:not(:disabled)', card);
      first && setTimeout(() => first.focus({ preventScroll: true }), 30);
    }
  });

  /* Card gallery thumbnails: hover (desktop) or tap swaps the main image; leaving the card restores it */
  const swapTo = (card, thumb) => {
    const main = $('.cx-card__img:not(.cx-card__img--alt)', card);
    if (!main || !thumb) return;
    if (!main.dataset.cxOrigSrc) {
      main.dataset.cxOrigSrc = main.currentSrc || main.src;
      main.dataset.cxOrigSrcset = main.getAttribute('srcset') || '';
    }
    main.srcset = thumb.dataset.srcset || '';
    main.src = thumb.dataset.src;
    main.classList.add('is-loaded');
    card.classList.add('is-thumb');
    $$('[data-cx-thumb]', card).forEach((b) => b.classList.toggle('is-active', b === thumb));
  };
  const restore = (card) => {
    const main = $('.cx-card__img:not(.cx-card__img--alt)', card);
    if (!main || !main.dataset.cxOrigSrc) return;
    main.srcset = main.dataset.cxOrigSrcset;
    main.src = main.dataset.cxOrigSrc;
    card.classList.remove('is-thumb');
    $$('[data-cx-thumb]', card).forEach((b) => b.classList.remove('is-active'));
  };
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  document.addEventListener('pointerover', (e) => {
    if (!fine.matches) return;
    const thumb = e.target.closest('[data-cx-thumb]');
    if (thumb) swapTo(thumb.closest('[data-cx-card]'), thumb);
  });
  document.addEventListener('pointerout', (e) => {
    if (!fine.matches) return;
    const card = e.target.closest('[data-cx-card]');
    if (card && card.classList.contains('is-thumb') && !card.contains(e.relatedTarget)) restore(card);
  });
  document.addEventListener('click', (e) => {
    const thumb = e.target.closest('[data-cx-thumb]');
    if (!thumb) return;
    e.preventDefault();
    swapTo(thumb.closest('[data-cx-card]'), thumb);
  });
})();